import { create } from 'zustand';
import { type PairingV1 } from '@hungryroot/rest';

interface RdpModalStore {
  rdpPairing: PairingV1 | null;
  toggleRdpModal: (pairing: PairingV1 | null) => void;
}

/**
 * Hook for managing RDP (Recipe Detail Page) modal state.
 *
 * This hook uses zustand for state management, allowing state to be shared
 * across components without prop drilling.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { rdpPairing, toggleRdpModal } = useRdpModal();
 *
 *   // Open modal with a pairing
 *   const handleOpenModal = (pairing: PairingV1) => {
 *     toggleRdpModal(pairing);
 *   };
 *
 *   // Close modal
 *   const handleCloseModal = () => {
 *     toggleRdpModal(null);
 *   };
 * }
 * ```
 *
 * @returns {Object} An object containing:
 *   - rdpPairing: The currently displayed pairing/recipe
 *   - toggleRdpModal: Function to open/close the modal
 */
export const useRdpModal = create<RdpModalStore>((set) => ({
  rdpPairing: null,
  toggleRdpModal: (pairing: PairingV1 | null) => set({ rdpPairing: pairing }),
}));
